// Payout structures and payments (Sections 5.3 and 5.4), plus the deal dates
// that drive them (Section 3.5).

import { addDays, addMonths, type ISODate } from '../dates';
import { round2, splitByPercent, sum } from '../money';
import { CommissionInputError } from './errors';

// ---------------------------------------------------------------------------
// Deal dates
// ---------------------------------------------------------------------------

export interface DealDates {
  leaseExecuted?: ISODate | null;
  commencementEstimated?: ISODate | null;
  commencementConfirmed?: ISODate | null;
  occupancy?: ISODate | null;
  contractEffective?: ISODate | null;
  ddEnd?: ISODate | null;
  closing?: ISODate | null;
}

/** Confirmed commencement wins over the estimate. */
export function effectiveCommencement(dates: DealDates): ISODate | null {
  return dates.commencementConfirmed ?? dates.commencementEstimated ?? null;
}

export interface LeaseExpiration {
  date: ISODate | null;
  basis: 'estimated' | 'confirmed' | null;
}

/** Expiration = commencement + term, ending the day before the anniversary. */
export function leaseExpiration(dates: DealDates, termMonths: number | null | undefined): LeaseExpiration {
  const commencement = effectiveCommencement(dates);
  if (!commencement || !termMonths) return { date: null, basis: null };
  return {
    date: addDays(addMonths(commencement, termMonths), -1),
    basis: dates.commencementConfirmed ? 'confirmed' : 'estimated',
  };
}

export const DEFAULT_DUE_DILIGENCE_DAYS = 45;
export const DEFAULT_CLOSING_DAYS_AFTER_DD = 30;

/** Under Contract dates: DD end defaults to +45 days, closing to +30 days after DD. Explicit dates win. */
export function underContractDates(dates: DealDates): { ddEnd: ISODate | null; closing: ISODate | null } {
  const effective = dates.contractEffective ?? null;
  const ddEnd = dates.ddEnd ?? (effective ? addDays(effective, DEFAULT_DUE_DILIGENCE_DAYS) : null);
  const closing = dates.closing ?? (ddEnd ? addDays(ddEnd, DEFAULT_CLOSING_DAYS_AFTER_DD) : null);
  return { ddEnd, closing };
}

// ---------------------------------------------------------------------------
// Payout structures
// ---------------------------------------------------------------------------

export type Trigger = 'lease_execution' | 'commencement' | 'occupancy' | 'closing' | 'custom';

/** Default days after the trigger that a payment is expected. */
export const DEFAULT_OFFSET_DAYS: Record<Trigger, number> = {
  lease_execution: 0,
  // The 2nd lease payment is due ON commencement (Brett, 2026-10-06): landlords owe it within
  // 30 days, but showing it due on the day keeps the broker invoicing and chasing promptly.
  commencement: 0,
  occupancy: 30,
  closing: 0,
  custom: 0,
};

export interface CashSplit {
  trigger: Trigger;
  pct: number;
  offsetDays?: number | null;
  customDate?: ISODate | null;
}

export type Payout =
  | { kind: 'cash_out'; splits: CashSplit[] }
  | {
      kind: 'annuity';
      monthlyAmount: number;
      months: number;
      trigger: Trigger;
      offsetDays?: number | null;
      customDate?: ISODate | null;
    }
  | { kind: 'at_closing'; offsetDays?: number | null };

export const PAYOUT_50_50: Payout = {
  kind: 'cash_out',
  splits: [
    { trigger: 'lease_execution', pct: 50 },
    { trigger: 'commencement', pct: 50 },
  ],
};

export const PAYOUT_100_AT_EXECUTION: Payout = {
  kind: 'cash_out',
  splits: [{ trigger: 'lease_execution', pct: 100 }],
};

export const PAYOUT_AT_CLOSING: Payout = { kind: 'at_closing' };

/** The date a trigger event happens (or is expected to), or null if not known yet. */
export function triggerDate(trigger: Trigger, dates: DealDates, customDate?: ISODate | null): ISODate | null {
  switch (trigger) {
    case 'lease_execution':
      return dates.leaseExecuted ?? null;
    case 'commencement':
      return effectiveCommencement(dates);
    case 'occupancy':
      // Until occupancy is known, the (estimated or confirmed) commencement stands in for it.
      return dates.occupancy ?? effectiveCommencement(dates);
    case 'closing':
      return dates.closing ?? underContractDates(dates).closing;
    case 'custom':
      return customDate ?? null;
  }
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

export type PaymentStatus = 'projected' | 'invoiced' | 'received';
export type EffectivePaymentStatus = PaymentStatus | 'overdue';

export interface PlannedPayment {
  /** Stable identity of this payment within the deal, used to regenerate without duplicates. */
  slotKey: string;
  label: string;
  amount: number;
  trigger: Trigger;
  offsetDays: number;
  /** Null until the trigger date is known. */
  expectedDate: ISODate | null;
}

export interface PaymentPlan {
  payments: PlannedPayment[];
  total: number;
  /** Annuity only: annuity total minus the calculated commission (shown, never blocking). */
  annuityDifference: number | null;
}

function expected(trigger: Trigger, offsetDays: number, dates: DealDates, customDate?: ISODate | null): ISODate | null {
  const base = triggerDate(trigger, dates, customDate);
  return base ? addDays(base, offsetDays) : null;
}

const TRIGGER_LABEL: Record<Trigger, string> = {
  lease_execution: 'at lease execution',
  commencement: 'at commencement',
  occupancy: 'at occupancy',
  closing: 'at closing',
  custom: 'on custom date',
};

/**
 * Turns the net commission and the payout structure into payment rows.
 * `commissionTotal` is the deal's net commission from calculateFees().
 */
export function planPayments(commissionTotal: number, payout: Payout, dates: DealDates): PaymentPlan {
  switch (payout.kind) {
    case 'cash_out': {
      if (payout.splits.length === 0) throw new CommissionInputError('A cash-out payout needs at least one payment.');
      const pctTotal = sum(payout.splits.map((s) => s.pct));
      if (Math.abs(pctTotal - 100) > 0.0001) {
        throw new CommissionInputError(`Payout percentages must add up to 100% (they add up to ${pctTotal}%).`);
      }
      const amounts = splitByPercent(commissionTotal, payout.splits.map((s) => s.pct));
      const payments = payout.splits.map((s, i): PlannedPayment => {
        const offsetDays = s.offsetDays ?? DEFAULT_OFFSET_DAYS[s.trigger];
        return {
          slotKey: `cash:${i + 1}:${s.trigger}`,
          label: `${s.pct}% ${TRIGGER_LABEL[s.trigger]}`,
          amount: amounts[i]!,
          trigger: s.trigger,
          offsetDays,
          expectedDate: expected(s.trigger, offsetDays, dates, s.customDate),
        };
      });
      return { payments, total: round2(commissionTotal), annuityDifference: null };
    }
    case 'annuity': {
      if (!Number.isInteger(payout.months) || payout.months < 1) {
        throw new CommissionInputError('An annuity needs a whole number of months (1 or more).');
      }
      if (!(payout.monthlyAmount > 0)) throw new CommissionInputError('An annuity needs a monthly amount.');
      const offsetDays = payout.offsetDays ?? 0;
      const first = expected(payout.trigger, offsetDays, dates, payout.customDate);
      const amount = round2(payout.monthlyAmount);
      const payments = Array.from({ length: payout.months }, (_, i): PlannedPayment => ({
        slotKey: `annuity:${i + 1}`,
        label: `Annuity ${i + 1} of ${payout.months}`,
        amount,
        trigger: payout.trigger,
        offsetDays,
        expectedDate: first ? addMonths(first, i) : null,
      }));
      const total = round2(amount * payout.months);
      return { payments, total, annuityDifference: round2(total - commissionTotal) };
    }
    case 'at_closing': {
      const offsetDays = payout.offsetDays ?? DEFAULT_OFFSET_DAYS.closing;
      return {
        payments: [
          {
            slotKey: 'closing',
            label: '100% at closing',
            amount: round2(commissionTotal),
            trigger: 'closing',
            offsetDays,
            expectedDate: expected('closing', offsetDays, dates),
          },
        ],
        total: round2(commissionTotal),
        annuityDifference: null,
      };
    }
  }
}

export interface StoredPayment {
  id: string;
  slotKey: string | null;
  status: PaymentStatus;
  amount: number;
  expectedDate: ISODate | null;
  receivedDate?: ISODate | null;
  receivedAmount?: number | null;
}

export interface PaymentChanges {
  toInsert: PlannedPayment[];
  toUpdate: { id: string; changes: Pick<PlannedPayment, 'label' | 'amount' | 'trigger' | 'offsetDays' | 'expectedDate'> }[];
  toDelete: string[];
  /** Invoiced and received payments: never changed or removed by regeneration. */
  untouched: string[];
}

/**
 * Works out how to bring stored payments in line with a fresh plan after deal
 * inputs or dates change. Only projected payments move; invoiced and received
 * payments are never silently changed.
 */
export function reconcilePayments(existing: StoredPayment[], planned: PlannedPayment[]): PaymentChanges {
  const locked = existing.filter((p) => p.status !== 'projected');
  const lockedSlots = new Set(locked.map((p) => p.slotKey).filter((k): k is string => k !== null));
  const plannedBySlot = new Map(planned.map((p) => [p.slotKey, p]));

  const changes: PaymentChanges = { toInsert: [], toUpdate: [], toDelete: [], untouched: locked.map((p) => p.id) };
  const matchedSlots = new Set<string>();

  for (const p of existing.filter((x) => x.status === 'projected')) {
    const plan = p.slotKey ? plannedBySlot.get(p.slotKey) : undefined;
    if (!plan || lockedSlots.has(plan.slotKey) || matchedSlots.has(plan.slotKey)) {
      changes.toDelete.push(p.id);
      continue;
    }
    matchedSlots.add(plan.slotKey);
    if (p.amount !== plan.amount || p.expectedDate !== plan.expectedDate) {
      changes.toUpdate.push({
        id: p.id,
        changes: {
          label: plan.label,
          amount: plan.amount,
          trigger: plan.trigger,
          offsetDays: plan.offsetDays,
          expectedDate: plan.expectedDate,
        },
      });
    }
  }
  for (const plan of planned) {
    if (!matchedSlots.has(plan.slotKey) && !lockedSlots.has(plan.slotKey)) changes.toInsert.push(plan);
  }
  return changes;
}

/** Received stays received; anything else past its expected date is overdue — whatever stage the deal is in. */
export function effectivePaymentStatus(
  p: { status: PaymentStatus; expectedDate: ISODate | null },
  today: ISODate,
): EffectivePaymentStatus {
  if (p.status === 'received') return 'received';
  // Overdue from the due date itself (Brett, 2026-10-15), so it gets invoiced and chased right away.
  if (p.expectedDate && p.expectedDate <= today) return 'overdue';
  return p.status;
}

/** A deal can move to Closed / Fully Paid (or Closed) only when every payment is received. */
export function closeCheck(payments: { id: string; status: PaymentStatus }[]): { ok: boolean; unpaid: string[] } {
  const unpaid = payments.filter((p) => p.status !== 'received').map((p) => p.id);
  return { ok: unpaid.length === 0, unpaid };
}
